import { useMemo } from 'react';

import { useEntitlements } from '@/hooks/useEntitlements';
import {
    normalizeCompanyExecutive,
    useFederatedSearchQueries,
    useLifecycleList,
    useTagSubjectsForTypes,
    type CompanyExecutive,
    type FederatedSearchProvider,
    type SearchMatchMode,
    type SearchMode,
} from '@/queries';
import { isJsonLogicRuleEmpty } from '@/shared/components/filters/jsonLogic';
import {
    buildSearchAdvancedFilterJsonLogic,
    getScopedSearchAdvancedFilter,
    getScopedSearchFilterOperators,
    getSearchFilterJoin,
    unwrapSearchFilters,
    type SearchFilterJoin,
} from '@/shared/components/filters/searchFilters';
import type { LifecycleInitiative } from '@/shared/components/lifecycles';
import type { SourceDocument, SourceDocumentNavigationAction } from '@/shared/components/lists/SourceDocumentReader';
import { isSearchDomainAvailable } from '@/shared/constants';
import { hasFeatureAddOnAccess, hasGatedFeatureAccess } from '@/shared/entitlements';
import {
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useMemoStore,
    usePipelineStore,
    useTeamStore,
} from '@/stores';
import { useBillingStore } from '@/stores/database/aliases/billing';
import {
    WorkspaceSubjectType,
    type Dashboard,
    type Folder,
    type Investment,
    type List,
    type Memo,
    type Pipeline,
    type SubjectReference,
    type Team,
} from '@/types';
import { corporateLogo, resolveCorporateLogo } from '@/utilities';

import {
    ALL_SEARCH_DOMAINS,
    AllSearchDomain,
    UnifiedSearchResult as BaseUnifiedSearchResult,
    DOCUMENT_SEARCH_SOURCE_KEYS,
    ECONOMIC_SEARCH_CATALOG,
} from '../../../../searchTabs';
import { hasCompanyScreenerCriteria } from '../02-Companies/01-CompaniesSearch/helpers';
import {
    collectEventRecords,
    companyMatchesFilters,
    documentResultMatchesFilters,
    normalizeEvent,
    workspaceResourceMatchesFilters,
} from './resultFilters';
import { hasSearchFilterValue } from './searchCriteria';

export { ALL_SEARCH_DOMAINS } from '../../../../searchTabs';
export type { AllSearchDomain } from '../../../../searchTabs';

export interface SearchPersonContext {
    company: {
        logoUrl?: string;
        name: string;
        symbol?: string;
        website?: string;
    };
    executive: CompanyExecutive;
    isExecutive: boolean;
}

export interface SearchDocumentContext {
    connectedFile?: { connectionId: string; fileId: string };
    mimeType?: string;
    navigationAction?: SourceDocumentNavigationAction;
    source: SourceDocument;
}

type WorkspaceSearchResource =
    | { kind: 'dashboards'; value: Dashboard }
    | { kind: 'folders'; value: Folder }
    | { kind: 'investments'; value: Investment }
    | { kind: 'lifecycles'; value: LifecycleInitiative }
    | { kind: 'lists'; value: List }
    | { kind: 'memos'; value: Memo }
    | { kind: 'pipelines'; value: Pipeline }
    | { kind: 'teams'; value: Team };

export interface UnifiedSearchResult extends BaseUnifiedSearchResult {
    company?: Record<string, unknown>;
    document?: SearchDocumentContext;
    person?: SearchPersonContext;
    workspaceResource?: WorkspaceSearchResource;
}
interface UseAllSearchInput {
    domains?: readonly AllSearchDomain[];
    filters: unknown;
    matchMode: SearchMatchMode;
    query: string;
    searchMode: SearchMode;
}
interface UseAllSearchResult {
    error?: string;
    loading: boolean;
    loadingDomains: AllSearchDomain[];
    results: UnifiedSearchResult[];
}
const DOMAIN_ORDER = new Map(ALL_SEARCH_DOMAINS.map((domain, index) => [domain, index]));
const PROVIDERS_BY_DOMAIN: Partial<Record<AllSearchDomain, readonly FederatedSearchProvider[]>> = {
    companies: ['companies'],
    documents: ['companies', 'connections', 'filings'],
    events: ['calendar'],
    news: ['events', 'fundraising'],
    people: ['connections', 'peopleEnrichment', 'executives'],
};
const TAG_SEARCH_SUBJECT_TYPES = [
    WorkspaceSubjectType.SEARCH_COMPANY,
    WorkspaceSubjectType.DASHBOARD,
    WorkspaceSubjectType.FOLDER,
    WorkspaceSubjectType.INVESTMENT,
    WorkspaceSubjectType.LIFECYCLE,
    WorkspaceSubjectType.LIST,
    WorkspaceSubjectType.MEMO,
    WorkspaceSubjectType.PIPELINE,
    WorkspaceSubjectType.TEAM,
] as const;
const WORKSPACE_SUBJECT_TYPE_BY_KIND: Record<WorkspaceSearchResource['kind'], WorkspaceSubjectType> = {
    dashboards: WorkspaceSubjectType.DASHBOARD,
    folders: WorkspaceSubjectType.FOLDER,
    investments: WorkspaceSubjectType.INVESTMENT,
    lifecycles: WorkspaceSubjectType.LIFECYCLE,
    lists: WorkspaceSubjectType.LIST,
    memos: WorkspaceSubjectType.MEMO,
    pipelines: WorkspaceSubjectType.PIPELINE,
    teams: WorkspaceSubjectType.TEAM,
};
const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);
const recordValue = (value: unknown): Record<string, unknown> => (isRecord(value) ? value : {});
const arrayValue = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const stringValue = (...values: unknown[]): string | undefined =>
    values.find((value): value is string => typeof value === 'string' && value.trim().length > 0)?.trim();
const booleanValue = (value: unknown) => value === true;
const formatSource = (source: string) =>
    source
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());
const matchesQuery = (values: unknown[], query: string, matchMode: SearchMatchMode) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return true;
    }
    return values.some((value) => {
        const normalizedValue = String(value ?? '')
            .trim()
            .toLowerCase();
        return matchMode === 'exact' ? normalizedValue === normalizedQuery : normalizedValue.includes(normalizedQuery);
    });
};
const nestedRecord = (record: Record<string, unknown>, ...path: string[]) =>
    path.reduce<Record<string, unknown>>((current, key) => recordValue(current[key]), record);
const subjectKey = (subject: Pick<SubjectReference, 'subjectId' | 'subjectType'>) =>
    `${subject.subjectType}:${subject.subjectId}`;
const selectedStrings = (record: Record<string, unknown>, key: string) =>
    arrayValue(record[key]).filter((value): value is string => typeof value === 'string');
const normalizeSourceKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
const normalizeWorkspaceResource = (resource: WorkspaceSearchResource): UnifiedSearchResult | undefined => {
    const { kind, value } = resource;
    const item = recordValue(value);
    const id = stringValue(item.id);
    const title = stringValue(item.editedName, item.name, item.title, item.label);
    if (!id || !title) {
        return undefined;
    }
    const website = stringValue(item.website);
    return {
        description: stringValue(item.description),
        domain: 'workspace',
        id: `workspace:${kind}:${id}`,
        kind,
        logoUrl: website ? corporateLogo(website) : undefined,
        metadata: [],
        source: 'Workspace',
        title,
        updatedAt: stringValue(item.updatedAt),
        url: `/${kind}/${id}`,
        workspaceResource: resource,
    };
};
const normalizeCompany = (value: unknown): UnifiedSearchResult | undefined => {
    const company = recordValue(value);
    const enrichment = recordValue(company.enrichment);
    const symbol = stringValue(enrichment.symbol, company.symbol);
    const title = stringValue(enrichment.companyName, company.companyName, company.name, symbol);
    const isNylasCompany = company.resultType === 'nylasCompany';
    const resultId = stringValue(company.entityId, symbol);
    if (!resultId || !title) {
        return undefined;
    }
    const website = stringValue(enrichment.website, company.website);
    const exchange = stringValue(enrichment.exchangeShortName, enrichment.exchange, company.exchange);
    const isFund = booleanValue(enrichment.isFund) || booleanValue(enrichment.isEtf);
    const isIndex = Boolean(symbol?.startsWith('^')) || Boolean(exchange?.toLowerCase().includes('index'));
    const kind = isNylasCompany ? 'Private Company' : isIndex ? 'Index' : isFund ? 'Fund' : 'Company';
    return {
        company: {
            ...company,
            enrichment,
        },
        description:
            stringValue(enrichment.description) ??
            (isNylasCompany ? 'Connected company from email and calendar relationships.' : undefined),
        domain: 'companies',
        exchange,
        id: `company:${resultId}`,
        kind,
        logoUrl: website ? corporateLogo(website) : undefined,
        metadata: [
            ...(exchange ? [{ label: 'Exchange', value: exchange }] : []),
            ...(isNylasCompany && website ? [{ label: 'Domain', value: website.replace(/^https?:\/\//, '') }] : []),
            ...(stringValue(enrichment.industry) ? [{ label: 'Industry', value: String(enrichment.industry) }] : []),
        ],
        source: isNylasCompany ? 'Nylas' : 'Platform Financials',
        symbol,
        title,
        url: isNylasCompany ? undefined : isFund ? `/search/funds/${symbol}` : `/search/companies/${symbol}`,
        website,
    };
};
const normalizeTaggedCompanySubject = (subject: SubjectReference): UnifiedSearchResult | undefined => {
    const website = stringValue(subject.metadata?.website);
    const exchange = stringValue(subject.metadata?.exchange);

    return normalizeCompany({
        companyName: subject.subjectLabel ?? subject.subjectId,
        enrichment: {
            companyName: subject.subjectLabel ?? subject.subjectId,
            exchange,
            symbol: subject.subjectId,
            website,
        },
        exchange,
        name: subject.subjectLabel ?? subject.subjectId,
        symbol: subject.subjectId,
        website,
    });
};
const normalizeCommandResult = (
    group: string,
    value: unknown,
    domain: 'documents' | 'people' | 'workspace',
): UnifiedSearchResult | undefined => {
    const item = recordValue(value);
    const id =
        domain === 'people'
            ? stringValue(item.email, item.id, item.name)
            : stringValue(item.id, item.webViewLink, item.name);
    const title = stringValue(item.name, item.title, item.label, item.email);
    if (!id || !title) {
        return undefined;
    }
    const webViewLink = stringValue(item.webViewLink, item.url);
    const source = stringValue(item.source) ?? formatSource(group);
    const connectionId = stringValue(item.connectedAccountId);
    const connectionType = stringValue(item.connectedAccountType);
    const resultId =
        domain === 'people'
            ? `people:${id.toLowerCase()}`
            : `${domain}:${group}:${connectionId ? `${connectionId}:` : ''}${id}`;
    const avatarUrl = stringValue(item.avatar, item.photoUrl, item.profilePicUrl, item.iconLink);
    const companyName = stringValue(item.company, item.organization, item.jobCompanyName);
    const companyWebsite = stringValue(item.companyWebsite, item.website);
    const email = stringValue(item.email);
    const documentContext: SearchDocumentContext | undefined =
        domain === 'documents'
            ? {
                  connectedFile:
                      connectionId && ['Google Drive', 'Google Sheets', 'Box', 'Dropbox'].includes(connectionType ?? '')
                          ? { connectionId, fileId: id }
                          : undefined,
                  mimeType: stringValue(item.mimeType, item.contentType, item.type),
                  navigationAction: webViewLink
                      ? {
                            external: true,
                            href: webViewLink,
                            label: `Open in ${source}`,
                        }
                      : undefined,
                  source: {
                      canonicalUrl: webViewLink,
                      kind: 'source',
                      label: `${source} Document`,
                      publishedAt: stringValue(item.modifiedTime, item.updatedAt, item.createdTime),
                      publisher: source,
                      sourceEntity: {
                          logoUrl: avatarUrl,
                          name: source,
                          website: webViewLink,
                      },
                      summary: stringValue(item.description, item.text, item.type),
                      title,
                  },
              }
            : undefined;
    return {
        description: stringValue(item.email, item.description, item.type),
        domain,
        document: documentContext,
        external: Boolean(webViewLink),
        id: resultId,
        kind: domain === 'people' ? 'Person' : domain === 'documents' ? 'Document' : 'Connected Data',
        logoUrl: avatarUrl,
        metadata: [],
        person:
            domain === 'people'
                ? {
                      company: {
                          logoUrl: companyWebsite ? corporateLogo(companyWebsite) : undefined,
                          name: companyName ?? 'Company unavailable',
                          website: companyWebsite,
                      },
                      executive: {
                          avatarUrl,
                          email,
                          id: resultId,
                          name: title,
                          phone: stringValue(item.phone, item.phoneNumber),
                          title: stringValue(item.jobTitle, item.role),
                      },
                      isExecutive: false,
                  }
                : undefined,
        source,
        title,
        updatedAt: stringValue(item.modifiedTime, item.updatedAt),
        url: webViewLink,
    };
};
const commandResultMatchesFilters = (
    result: UnifiedSearchResult,
    filters: Record<string, unknown>,
    sourceKey: string,
    rawValue?: unknown,
    filterJoin: SearchFilterJoin = 'any',
) => {
    const sources = selectedStrings(filters, 'sources').map(normalizeSourceKey);
    const hasEnrichmentFilters = [
        'educationLevels',
        'email',
        'firstName',
        'jobTitles',
        'keywords',
        'lastName',
        'roles',
    ].some((key) => Boolean(stringValue(filters[key]) || selectedStrings(filters, key).length));
    if (result.domain === 'documents') {
        return documentResultMatchesFilters(result, filters, rawValue, filterJoin);
    }
    const predicates = [
        result.domain === 'people' && hasEnrichmentFilters ? false : undefined,
        sources.length
            ? sources.includes(normalizeSourceKey(sourceKey)) || sources.includes(normalizeSourceKey(result.source))
            : undefined,
    ].filter((predicate): predicate is boolean => predicate !== undefined);

    return predicates.length === 0 || (filterJoin === 'all' ? predicates.every(Boolean) : predicates.some(Boolean));
};
const fmpExecutiveMatchesFilters = (
    result: UnifiedSearchResult,
    filters: Record<string, unknown>,
    filterJoin: SearchFilterJoin = 'any',
) => {
    const person = result.person;

    if (!person) {
        return false;
    }

    const normalizedName = person.executive.name.toLocaleLowerCase();
    const nameParts = normalizedName.split(/\s+/).filter(Boolean);
    const firstName = stringValue(filters.firstName)?.toLocaleLowerCase();
    const lastName = stringValue(filters.lastName)?.toLocaleLowerCase();
    const email = stringValue(filters.email)?.toLocaleLowerCase();
    const jobTitles = selectedStrings(filters, 'jobTitles').map((value) => value.toLocaleLowerCase());
    const keywords = selectedStrings(filters, 'keywords').map((value) => value.toLocaleLowerCase());
    const searchableValues = [
        person.executive.name,
        person.executive.title,
        person.executive.location,
        person.company.name,
        person.company.symbol,
    ]
        .filter((value): value is string => Boolean(value))
        .join(' ')
        .toLocaleLowerCase();

    const educationLevels = selectedStrings(filters, 'educationLevels');
    const roles = selectedStrings(filters, 'roles');
    const predicates = [
        firstName ? Boolean(nameParts[0]?.includes(firstName)) : undefined,
        lastName ? Boolean(nameParts.at(-1)?.includes(lastName)) : undefined,
        email ? Boolean(person.executive.email?.toLocaleLowerCase().includes(email)) : undefined,
        jobTitles.length
            ? jobTitles.some((title) => Boolean(person.executive.title?.toLocaleLowerCase().includes(title)))
            : undefined,
        keywords.length ? keywords.every((keyword) => searchableValues.includes(keyword)) : undefined,
        educationLevels.length ? false : undefined,
        roles.length ? false : undefined,
    ].filter((predicate): predicate is boolean => predicate !== undefined);

    return predicates.length === 0 || (filterJoin === 'all' ? predicates.every(Boolean) : predicates.some(Boolean));
};
const normalizeFiling = (value: unknown, index: number): UnifiedSearchResult | undefined => {
    const wrapper = recordValue(value);
    const filing = recordValue(wrapper.filing);
    const symbol = stringValue(wrapper.symbol, filing.symbol);
    const filingType = stringValue(filing.formType, filing.type, filing.form);
    const filingUrl = stringValue(filing.finalLink, filing.link);
    if (!symbol || !filingType) {
        return undefined;
    }
    const params = new URLSearchParams();
    const filingDate = stringValue(filing.filingDate, filing.fillingDate);
    const acceptedDate = stringValue(filing.acceptedDate);
    if (filingDate) {
        params.set('filingDate', filingDate);
    }
    if (acceptedDate) {
        params.set('acceptedDate', acceptedDate);
    }
    if (filingUrl) {
        params.set('filingUrl', filingUrl);
    }
    params.set('filingType', filingType);
    const destination = `/search/companies/${symbol}/filings?${params.toString()}`;
    const description = stringValue(filing.description) ?? `Public filing submitted by ${symbol}.`;
    return {
        description,
        domain: 'documents',
        document: {
            navigationAction: {
                href: destination,
                label: `View ${symbol} Filings`,
            },
            source: {
                canonicalUrl: filingUrl,
                kind: 'source',
                label: `${filingType} Filing`,
                publishedAt: filingDate ?? acceptedDate,
                publisher: 'SEC',
                sourceEntity: { name: symbol },
                summary: description,
                title: `${symbol} ${filingType}`,
            },
        },
        id: `filing:${symbol}:${stringValue(filing.accessionNumber, filingUrl, filingDate) ?? index}`,
        kind: filingType,
        metadata: [{ label: 'Symbol', value: symbol }],
        source: 'SEC / Platform Financials',
        title: `${symbol} ${filingType}`,
        updatedAt: filingDate ?? acceptedDate,
        url: destination,
    };
};
const normalizeEnrichedPerson = (value: unknown): UnifiedSearchResult | undefined => {
    const person = recordValue(value);
    const id = stringValue(person.work_email, arrayValue(person.personal_emails)[0], person.id);
    const title = stringValue(person.full_name, person.work_email);
    if (!id || !title) {
        return undefined;
    }
    const linkedin = stringValue(person.linkedin_url);
    const company = stringValue(person.job_company_name);
    const companyWebsite = stringValue(person.job_company_website, person.job_company_website_url);
    const email = stringValue(person.work_email, arrayValue(person.personal_emails)[0]);
    const resultId = `people:${id.toLowerCase()}`;
    const avatarUrl = stringValue(person.profile_pic_url);
    return {
        description: [stringValue(person.job_title), company].filter(Boolean).join(' at '),
        domain: 'people',
        external: Boolean(linkedin),
        id: resultId,
        kind: 'Enriched Person',
        logoUrl: avatarUrl,
        metadata: [
            ...(company ? [{ label: 'Company', value: company }] : []),
            ...(email ? [{ label: 'Email', value: email }] : []),
            ...(stringValue(person.location_name) ? [{ label: 'Location', value: String(person.location_name) }] : []),
        ],
        person: {
            company: {
                logoUrl: companyWebsite ? corporateLogo(companyWebsite) : undefined,
                name: company ?? 'Company unavailable',
                website: companyWebsite,
            },
            executive: {
                avatarUrl,
                email,
                id: resultId,
                linkedinUrl: linkedin,
                location: stringValue(person.location_name),
                name: title,
                phone: stringValue(arrayValue(person.phone_numbers)[0]),
                title: stringValue(person.job_title),
                website: stringValue(person.personal_website),
            },
            isExecutive: false,
        },
        source: 'People Data Labs',
        title,
        url: linkedin ? (linkedin.startsWith('http') ? linkedin : `https://${linkedin}`) : undefined,
    };
};
const normalizeFmpExecutive = (value: unknown, index: number): UnifiedSearchResult | undefined => {
    const result = recordValue(value);
    const company = recordValue(result.company);
    const executive = normalizeCompanyExecutive(result.executive, index);
    const symbol = stringValue(company.symbol);
    const companyName = stringValue(company.name, symbol);

    if (!executive || !symbol || !companyName) {
        return undefined;
    }

    const resultId = `people:fmp:${symbol}:${executive.id}`;
    const website = stringValue(company.website);
    const logoUrl = executive.avatarUrl;

    return {
        description: [executive.title, companyName].filter(Boolean).join(' at '),
        domain: 'people',
        id: resultId,
        kind: 'Company Executive',
        logoUrl,
        metadata: [
            { label: 'Company', value: companyName },
            { label: 'Symbol', value: symbol },
        ],
        person: {
            company: {
                logoUrl: resolveCorporateLogo(website, stringValue(company.logoUrl)),
                name: companyName,
                symbol,
                website,
            },
            executive: { ...executive, id: resultId },
            isExecutive: true,
        },
        source: 'Platform Financials',
        title: executive.name,
    };
};
const buildEconomicResults = (
    query: string,
    matchMode: SearchMatchMode,
    filters: Record<string, unknown>,
    filterJoin: SearchFilterJoin = 'any',
): UnifiedSearchResult[] => {
    const categories = selectedStrings(filters, 'categories');
    const sources = selectedStrings(filters, 'sources');
    const frequencies = selectedStrings(filters, 'frequencies');
    return ECONOMIC_SEARCH_CATALOG.filter((item) => {
        const itemSources = item.category === 'macro' ? ['fred', 'fmp'] : ['fmp'];
        const frequency = 'frequency' in item ? item.frequency : item.category === 'macro' ? 'monthly' : 'daily';
        const predicates = [
            categories.length ? categories.includes(item.category) : undefined,
            sources.length ? sources.some((source) => itemSources.includes(source)) : undefined,
            frequencies.length ? frequencies.includes(frequency) : undefined,
        ].filter((predicate): predicate is boolean => predicate !== undefined);

        return (
            (predicates.length === 0 ||
                (filterJoin === 'all' ? predicates.every(Boolean) : predicates.some(Boolean))) &&
            matchesQuery([item.title, item.id, item.category], query, matchMode)
        );
    }).map((item) => ({
        domain: 'economic',
        id: `economic:${item.category}:${item.id}`,
        kind: item.category === 'macro' ? 'Economic Indicator' : item.category === 'treasury' ? 'Treasury' : 'Market',
        metadata: [],
        source: item.category === 'macro' ? 'FRED / Platform Financials' : 'Platform Financials',
        title: item.title,
        url:
            item.category === 'macro'
                ? `/search/economic/us/macro/${item.id}`
                : item.category === 'treasury'
                  ? `/search/economic/us/treasury/${item.id}`
                  : `/search/economic?q=${encodeURIComponent(item.title)}`,
    }));
};
const dedupeAndSort = (results: UnifiedSearchResult[]) => {
    const uniqueResults = Array.from(new Map(results.map((result) => [result.id, result])).values());
    return uniqueResults.sort(
        (first, second) =>
            (DOMAIN_ORDER.get(first.domain) ?? 0) - (DOMAIN_ORDER.get(second.domain) ?? 0) ||
            first.title.localeCompare(second.title),
    );
};
export const useAllSearch = ({
    domains = ALL_SEARCH_DOMAINS,
    filters,
    matchMode,
    query,
    searchMode,
}: UseAllSearchInput): UseAllSearchResult => {
    const activeDomains = useMemo(() => new Set(domains.filter(isSearchDomainAvailable)), [domains]);
    const folders = useFolderStore((state) => state.folders);
    const investments = useInvestmentStore((state) => state.investments);
    const memos = useMemoStore((state) => state.memos);
    const pipelines = usePipelineStore((state) => state.pipelines);
    const teams = useTeamStore((state) => state.teams);
    const dashboards = useDashboardStore((state) => state.dashboards);
    const lists = useListStore((state) => state.lists);
    const { hasFeature } = useEntitlements();
    const canUseAdvancedFilters = hasFeature('advancedFilters');
    const billingSnapshot = useBillingStore((state) => state.snapshot);
    const lifecyclesEnabled = hasFeatureAddOnAccess(billingSnapshot, 'lifecycles');
    const lifecycleSearchEnabled = lifecyclesEnabled && activeDomains.has('workspace');
    const lifecycleListQuery = useLifecycleList({ enabled: lifecycleSearchEnabled });
    const lifecycles = useMemo(() => lifecycleListQuery.data?.items ?? [], [lifecycleListQuery.data?.items]);
    const legacyFilters = useMemo(() => recordValue(unwrapSearchFilters(filters)), [filters]);
    const filterJoin = getSearchFilterJoin(filters);
    const companyFilterOperators = useMemo(
        () => getScopedSearchFilterOperators(filters, ['searchDomains', 'companies']),
        [filters],
    );
    const companyAdvancedFilter = useMemo(() => getScopedSearchAdvancedFilter(filters, 'companies'), [filters]);
    const companyAdvancedFilterJsonLogic = useMemo(
        () => (canUseAdvancedFilters ? buildSearchAdvancedFilterJsonLogic(companyAdvancedFilter) : undefined),
        [canUseAdvancedFilters, companyAdvancedFilter],
    );
    const hasCompanyAdvancedFilters = !isJsonLogicRuleEmpty(companyAdvancedFilterJsonLogic);
    const domainFilters = useMemo(() => nestedRecord(legacyFilters, 'searchDomains'), [legacyFilters]);
    const peopleFilters = recordValue(domainFilters.people);
    const companyFilters = recordValue(domainFilters.companies);
    const documentFilters = recordValue(domainFilters.documents);
    const economicFilters = recordValue(domainFilters.economic);
    const newsFilters = recordValue(domainFilters.news);
    const eventFilters = recordValue(domainFilters.events);
    const workspaceFilters = recordValue(domainFilters.workspace);
    const tradeFilters = recordValue(domainFilters.trade);
    const selectedTagIds = useMemo(
        () =>
            Array.from(
                new Set([
                    ...selectedStrings(legacyFilters, 'workspaceTags'),
                    ...selectedStrings(companyFilters, 'workspaceTags'),
                    ...selectedStrings(workspaceFilters, 'tags'),
                ]),
            ).sort(),
        [companyFilters, legacyFilters, workspaceFilters],
    );
    const tagSubjectsQuery = useTagSubjectsForTypes(selectedTagIds, TAG_SEARCH_SUBJECT_TYPES, filterJoin === 'all');
    const matchingTagSubjects = useMemo(() => tagSubjectsQuery.data ?? [], [tagSubjectsQuery.data]);
    const matchingTagSubjectKeys = useMemo(() => new Set(matchingTagSubjects.map(subjectKey)), [matchingTagSubjects]);
    const effectiveWorkspaceFilters = useMemo(
        () => (selectedTagIds.length ? { ...workspaceFilters, tags: selectedTagIds } : workspaceFilters),
        [selectedTagIds, workspaceFilters],
    );
    const peopleSources = selectedStrings(peopleFilters, 'sources').map(normalizeSourceKey);
    const emailAndCalendarEnabled = hasGatedFeatureAccess(billingSnapshot, 'emailAndCalendar');
    const enrichmentEnabled =
        hasGatedFeatureAccess(billingSnapshot, 'dataEnrichment') &&
        (!peopleSources.length || peopleSources.includes('enrichment'));
    const fmpExecutivesEnabled = !peopleSources.length || peopleSources.includes('fmpexecutives');
    const connectionDomains = useMemo(() => {
        const requested: ('documents' | 'people')[] = [];
        if (emailAndCalendarEnabled && activeDomains.has('documents')) {
            requested.push('documents');
        }
        if (emailAndCalendarEnabled && activeDomains.has('people')) {
            requested.push('people');
        }
        return requested;
    }, [activeDomains, emailAndCalendarEnabled]);
    const enabledProviders = useMemo(() => {
        const providers: FederatedSearchProvider[] = [];
        if (activeDomains.has('companies') || activeDomains.has('documents')) {
            providers.push('companies');
        }
        if (emailAndCalendarEnabled && (activeDomains.has('people') || activeDomains.has('documents'))) {
            providers.push('connections');
        }
        if (activeDomains.has('people') && enrichmentEnabled) {
            providers.push('peopleEnrichment');
        }
        if (activeDomains.has('people') && fmpExecutivesEnabled) {
            providers.push('executives');
        }
        if (activeDomains.has('documents')) {
            providers.push('filings');
        }
        if (activeDomains.has('news')) {
            providers.push('events', 'fundraising');
        }
        if (activeDomains.has('events')) {
            providers.push('calendar');
        }
        return providers;
    }, [activeDomains, emailAndCalendarEnabled, enrichmentEnabled, fmpExecutivesEnabled]);
    const filterEnabledProviders = useMemo(() => {
        const providers: FederatedSearchProvider[] = [];

        if (
            activeDomains.has('companies') &&
            (hasCompanyScreenerCriteria(companyFilters) || hasCompanyAdvancedFilters)
        ) {
            providers.push('companies');
        }
        if (activeDomains.has('people') && hasSearchFilterValue(peopleFilters)) {
            if (emailAndCalendarEnabled) {
                providers.push('connections');
            }
            if (enrichmentEnabled) {
                providers.push('peopleEnrichment');
            }
        }
        if (emailAndCalendarEnabled && activeDomains.has('documents') && hasSearchFilterValue(documentFilters)) {
            providers.push('connections');
        }
        if (activeDomains.has('news') && hasSearchFilterValue(newsFilters)) {
            providers.push('events', 'fundraising');
        }
        if (activeDomains.has('events') && hasSearchFilterValue(eventFilters)) {
            providers.push('calendar');
        }

        return providers;
    }, [
        activeDomains,
        companyFilters,
        documentFilters,
        emailAndCalendarEnabled,
        enrichmentEnabled,
        eventFilters,
        hasCompanyAdvancedFilters,
        newsFilters,
        peopleFilters,
    ]);
    const providerQueries = useFederatedSearchQueries({
        companyAdvancedFilterJsonLogic,
        companyFilterOperators,
        companyFilters,
        connectionDomains,
        enabledProviders,
        filterEnabledProviders,
        filterJoin,
        matchMode,
        peopleFilters,
        query,
        searchMode,
    });
    const localResults = useMemo(() => {
        const results: UnifiedSearchResult[] = [];
        if (
            activeDomains.has('workspace') &&
            (query.trim() || hasSearchFilterValue(effectiveWorkspaceFilters) || selectedTagIds.length)
        ) {
            const workspaceResources: WorkspaceSearchResource[] = [
                ...folders.map((value) => ({ kind: 'folders' as const, value })),
                ...investments.map((value) => ({ kind: 'investments' as const, value })),
                ...lifecycles.map((value) => ({ kind: 'lifecycles' as const, value })),
                ...memos.map((value) => ({ kind: 'memos' as const, value })),
                ...pipelines.map((value) => ({ kind: 'pipelines' as const, value })),
                ...teams.map((value) => ({ kind: 'teams' as const, value })),
                ...dashboards.map((value) => ({ kind: 'dashboards' as const, value })),
                ...lists.map((value) => ({ kind: 'lists' as const, value })),
            ];
            workspaceResources.forEach((resource) => {
                const resourceSubjectKey = subjectKey({
                    subjectId: String(resource.value.id),
                    subjectType: WORKSPACE_SUBJECT_TYPE_BY_KIND[resource.kind],
                });
                if (
                    !workspaceResourceMatchesFilters(
                        resource.value,
                        effectiveWorkspaceFilters,
                        filterJoin,
                        resource.kind,
                        selectedTagIds.length ? matchingTagSubjectKeys.has(resourceSubjectKey) : undefined,
                    )
                ) {
                    return;
                }
                const result = normalizeWorkspaceResource(resource);
                if (result && matchesQuery([result.title, result.description, result.kind], query, matchMode)) {
                    results.push(result);
                }
            });
        }
        if (
            activeDomains.has('companies') &&
            selectedTagIds.length &&
            !query.trim() &&
            !hasCompanyScreenerCriteria(companyFilters)
        ) {
            matchingTagSubjects
                .filter((subject) => subject.subjectType === WorkspaceSubjectType.SEARCH_COMPANY)
                .forEach((subject) => {
                    const result = normalizeTaggedCompanySubject(subject);

                    if (result) {
                        results.push(result);
                    }
                });
        }
        if (activeDomains.has('economic') && (query.trim() || hasSearchFilterValue(economicFilters))) {
            results.push(...buildEconomicResults(query, matchMode, economicFilters, filterJoin));
        }
        if (activeDomains.has('trade') && (query.trim() || hasSearchFilterValue(tradeFilters))) {
            const tradeLabel = query.trim() || 'matching filters';
            results.push({
                description: 'Open the global trade map with this query and inspect matching corridors and hubs.',
                domain: 'trade',
                id: `trade:${query.trim().toLowerCase() || JSON.stringify(tradeFilters)}`,
                kind: 'Global Trade Query',
                metadata: [],
                source: 'Global Trade Dataset',
                title: `Explore ${query.trim() ? `“${tradeLabel}”` : tradeLabel} in Global Trade`,
                url: `/search/trade${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ''}`,
            });
        }
        return results;
    }, [
        activeDomains,
        companyFilters,
        dashboards,
        economicFilters,
        effectiveWorkspaceFilters,
        filterJoin,
        folders,
        investments,
        lifecycles,
        lists,
        matchMode,
        matchingTagSubjectKeys,
        matchingTagSubjects,
        memos,
        pipelines,
        query,
        selectedTagIds,
        teams,
        tradeFilters,
    ]);
    const remoteResults = useMemo(() => {
        const results: UnifiedSearchResult[] = [];
        const companyFilters = recordValue(domainFilters.companies);
        const documentFilters = recordValue(domainFilters.documents);
        const eventFilters = recordValue(domainFilters.events);
        const peopleFilters = recordValue(domainFilters.people);
        const newsFilters = recordValue(domainFilters.news);
        const documentTypes = selectedStrings(documentFilters, 'types');
        providerQueries.forEach(({ data, provider }) => {
            if (provider === 'calendar' && activeDomains.has('events')) {
                const sources = selectedStrings(eventFilters, 'sources');
                const impacts = selectedStrings(eventFilters, 'impacts');
                Object.entries(recordValue(data)).forEach(([source, values]) => {
                    collectEventRecords(values).forEach((event, index) => {
                        const impact = stringValue(event.impact)?.toLowerCase() ?? 'unknown';
                        const predicates = [
                            sources.length ? sources.includes(source) : undefined,
                            impacts.length ? source === 'economic' && impacts.includes(impact) : undefined,
                        ].filter((predicate): predicate is boolean => predicate !== undefined);

                        if (
                            predicates.length > 0 &&
                            !(filterJoin === 'all' ? predicates.every(Boolean) : predicates.some(Boolean))
                        ) {
                            return;
                        }
                        if (!matchesQuery(Object.values(event), query, matchMode)) {
                            return;
                        }
                        const result = normalizeEvent(event, source, index, 'events');
                        if (result) {
                            results.push(result);
                        }
                    });
                });
            }
            if (provider === 'companies') {
                arrayValue(data).forEach((companyValue) => {
                    if (!companyMatchesFilters(companyValue, companyFilters, filterJoin, companyFilterOperators)) {
                        return;
                    }
                    const companyResult = normalizeCompany(companyValue);
                    const companyRecord = recordValue(companyValue);
                    const symbol = stringValue(
                        recordValue(companyRecord.enrichment).symbol,
                        companyRecord.symbol,
                        companyRecord.entityId,
                    );
                    if (
                        selectedTagIds.length &&
                        (!symbol ||
                            !matchingTagSubjectKeys.has(
                                subjectKey({
                                    subjectId: symbol,
                                    subjectType: WorkspaceSubjectType.SEARCH_COMPANY,
                                }),
                            ))
                    ) {
                        return;
                    }
                    if (companyResult && activeDomains.has('companies')) {
                        results.push(companyResult);
                    }
                });
            }
            if (
                provider === 'filings' &&
                activeDomains.has('documents') &&
                (!documentTypes.length || documentTypes.includes('publicFilings'))
            ) {
                arrayValue(data).forEach((filing, index) => {
                    const result = normalizeFiling(filing, index);
                    const raw = recordValue(recordValue(filing).filing);
                    if (
                        result &&
                        documentResultMatchesFilters(
                            result,
                            documentFilters,
                            { ...raw, publishedAt: result.updatedAt, mimeType: raw.mimeType ?? 'text/html' },
                            filterJoin,
                        )
                    ) {
                        results.push(result);
                    }
                });
            }
            if (provider === 'connections') {
                Object.entries(recordValue(data)).forEach(([group, values]) => {
                    if (group === 'people' && activeDomains.has('people')) {
                        arrayValue(values).forEach((value) => {
                            const result = normalizeCommandResult(group, value, 'people');
                            if (
                                result &&
                                commandResultMatchesFilters(result, peopleFilters, group, value, filterJoin)
                            ) {
                                results.push(result);
                            }
                        });
                    }
                    if (
                        DOCUMENT_SEARCH_SOURCE_KEYS.has(group) &&
                        activeDomains.has('documents') &&
                        (!documentTypes.length || documentTypes.includes('connected'))
                    ) {
                        arrayValue(values).forEach((value) => {
                            const result = normalizeCommandResult(group, value, 'documents');
                            if (
                                result &&
                                commandResultMatchesFilters(result, documentFilters, group, value, filterJoin)
                            ) {
                                results.push(result);
                            }
                        });
                    }
                });
            }
            if (provider === 'peopleEnrichment' && activeDomains.has('people')) {
                arrayValue(recordValue(data).data).forEach((person) => {
                    const result = normalizeEnrichedPerson(person);
                    if (result) {
                        results.push(result);
                    }
                });
            }
            if (provider === 'executives' && activeDomains.has('people')) {
                arrayValue(data).forEach((executive, index) => {
                    const result = normalizeFmpExecutive(executive, index);
                    if (result && fmpExecutiveMatchesFilters(result, peopleFilters, filterJoin)) {
                        results.push(result);
                    }
                });
            }
            if (provider === 'events' || provider === 'fundraising') {
                type NewsProviderRecord = {
                    event: Record<string, unknown>;
                    feed?: string;
                    type: string;
                };
                const newsTypes = selectedStrings(newsFilters, 'types');
                const feeds = selectedStrings(newsFilters, 'feeds');
                const providerRecords: NewsProviderRecord[] =
                    provider === 'fundraising'
                        ? collectEventRecords(data).map((event) => ({ event, feed: undefined, type: 'fundraising' }))
                        : Object.entries(recordValue(data)).flatMap<NewsProviderRecord>(([group, values]) => {
                              const groupType = group === 'news' ? 'market' : group;
                              if (group === 'news') {
                                  return Object.entries(recordValue(values)).flatMap(([feed, feedValues]) =>
                                      collectEventRecords(feedValues).map((event) => ({
                                          event,
                                          feed,
                                          type: groupType,
                                      })),
                                  );
                              }
                              return collectEventRecords(values).map((event) => ({
                                  event,
                                  feed: undefined,
                                  type: groupType,
                              }));
                          });
                providerRecords.forEach(({ event, feed, type }, index) => {
                    const predicates = [
                        newsTypes.length ? newsTypes.includes(type) : undefined,
                        feeds.length ? Boolean(feed && feeds.includes(feed)) : undefined,
                    ].filter((predicate): predicate is boolean => predicate !== undefined);

                    if (
                        predicates.length > 0 &&
                        !(filterJoin === 'all' ? predicates.every(Boolean) : predicates.some(Boolean))
                    ) {
                        return;
                    }
                    if (!matchesQuery(Object.values(event), query, matchMode)) {
                        return;
                    }
                    const result = normalizeEvent(event, provider, index);
                    if (result) {
                        results.push(result);
                    }
                });
            }
        });
        return dedupeAndSort(results);
    }, [
        activeDomains,
        companyFilterOperators,
        domainFilters,
        filterJoin,
        matchMode,
        matchingTagSubjectKeys,
        providerQueries,
        query,
        selectedTagIds.length,
    ]);
    const providerLoading = providerQueries.some(
        ({ isFetching, provider }) => enabledProviders.includes(provider) && isFetching,
    );
    const loading =
        providerLoading || tagSubjectsQuery.isFetching || (lifecycleSearchEnabled && lifecycleListQuery.isFetching);
    const loadingDomains = useMemo(() => {
        const providerLoadingDomains = domains.filter((domain) => {
            const domainProviders = PROVIDERS_BY_DOMAIN[domain] ?? [];

            return providerQueries.some(
                ({ isFetching, provider }) =>
                    domainProviders.includes(provider) && enabledProviders.includes(provider) && isFetching,
            );
        });

        if (!tagSubjectsQuery.isFetching && (!lifecycleSearchEnabled || !lifecycleListQuery.isFetching)) {
            return providerLoadingDomains;
        }

        return Array.from(
            new Set([
                ...providerLoadingDomains,
                ...(activeDomains.has('companies') ? (['companies'] as const) : []),
                ...(activeDomains.has('workspace') ? (['workspace'] as const) : []),
            ]),
        );
    }, [
        activeDomains,
        domains,
        enabledProviders,
        lifecycleListQuery.isFetching,
        lifecycleSearchEnabled,
        providerQueries,
        tagSubjectsQuery.isFetching,
    ]);
    const failedProviderCount = providerQueries.filter(
        ({ error: providerError, provider }) => enabledProviders.includes(provider) && providerError,
    ).length;
    const failedSourceCount =
        failedProviderCount +
        (tagSubjectsQuery.isError ? 1 : 0) +
        (lifecycleSearchEnabled && lifecycleListQuery.isError ? 1 : 0);
    const sourceCount = enabledProviders.length + (selectedTagIds.length ? 1 : 0) + (lifecycleSearchEnabled ? 1 : 0);
    const results = useMemo(() => dedupeAndSort([...localResults, ...remoteResults]), [localResults, remoteResults]);
    return {
        error:
            failedSourceCount === sourceCount && failedSourceCount > 0
                ? 'Search providers are unavailable.'
                : failedSourceCount > 0
                  ? 'Some search providers are temporarily unavailable.'
                  : undefined,
        loading,
        loadingDomains,
        results,
    };
};
